type AnimationValue = number | string
type AnimationState = Record<string, AnimationValue>
type AnimationStateOrFn = AnimationState | ((ctx: { isFullSize: boolean }) => AnimationState)

export interface ModalAnimationConfig {
	initial: AnimationStateOrFn
	animate: AnimationStateOrFn
	exit: AnimationStateOrFn
	transition: Record<string, AnimationValue | number[]>
	originClass?: string
}

const SPRING = {
	type: 'spring' as const,
	stiffness: 320,
	damping: 32,
	mass: 0.9,
}

const EASE_OUT = {
	duration: 0.3,
	ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
}

export const MODAL_ANIMATIONS = {
	default: {
		initial: ({ isFullSize }) => ({ opacity: 0, scale: isFullSize ? 1 : 0.95, y: 16 }),
		animate: { opacity: 1, scale: 1, y: 0 },
		exit: ({ isFullSize }) => ({ opacity: 0, scale: isFullSize ? 1 : 0.95, y: 8 }),
		transition: SPRING,
	},
	fade: {
		initial: { opacity: 0 },
		animate: { opacity: 1 },
		exit: { opacity: 0 },
		transition: EASE_OUT,
	},
	'slide-from-bottom': {
		initial: { opacity: 0, y: '100%' },
		animate: { opacity: 1, y: 0 },
		exit: { opacity: 0, y: '100%' },
		transition: SPRING,
	},
	'slide-from-top': {
		initial: { opacity: 0, y: '-100%' },
		animate: { opacity: 1, y: 0 },
		exit: { opacity: 0, y: '-100%' },
		transition: SPRING,
	},
	'slide-from-left': {
		initial: { opacity: 0, x: '-100%' },
		animate: { opacity: 1, x: 0 },
		exit: { opacity: 0, x: '-100%' },
		transition: SPRING,
		originClass: 'origin-left',
	},
	'slide-from-right': {
		initial: { opacity: 0, x: '100%' },
		animate: { opacity: 1, x: 0 },
		exit: { opacity: 0, x: '100%' },
		transition: SPRING,
		originClass: 'origin-right',
	},
	zoom: {
		initial: { opacity: 0, scale: 0.85 },
		animate: { opacity: 1, scale: 1 },
		exit: { opacity: 0, scale: 0.9 },
		transition: SPRING,
	},
} satisfies Record<string, ModalAnimationConfig>

export type ModalAnimationName = keyof typeof MODAL_ANIMATIONS

export function resolveAnimationState(
	state: AnimationStateOrFn,
	ctx: { isFullSize: boolean },
): AnimationState {
	return typeof state === 'function' ? state(ctx) : state
}
